import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { Json, Database } from '@/integrations/supabase/types';
import { Order, OrderItem, MenuItem } from '@/types';

// --- Type Definitions ---
export interface SortOrderItem {
  id: string;
  sort_order: number;
}

export interface NewCategory {
  name: string;
  description?: string;
  image_url?: string;
  branch_id: string;
}

export interface UpdatedCategory {
  id: string;
  name?: string;
  description?: string;
  image_url?: string;
  is_active?: boolean;
}

export interface NewMenuItem {
  category_id: string;
  name: string;
  description?: string;
  image_url?: string;
  base_price: number;
  is_available?: boolean;
}

export interface UpdatedMenuItem {
  id: string;
  name?: string;
  description?: string;
  image_url?: string;
  base_price?: number;
  is_available?: boolean;
}

export type NewPopOffer = Database['public']['Tables']['pop_offers']['Insert'];
export type UpdatedPopOffer = Database['public']['Tables']['pop_offers']['Update'];
export type PopOfferRow = Database['public']['Tables']['pop_offers']['Row'];

export interface NewMenuItemVariant {
  menu_item_id: string
  size_name: string
  price: number
  description?: string | null  // Optional variant-specific description
}

// --- API Error Handling ---
export class SupabaseServiceError extends Error {
  constructor(message: string, public originalError?: unknown) {
    super(message);
    this.name = 'SupabaseServiceError';
    Object.setPrototypeOf(this, SupabaseServiceError.prototype);
  }
}

// --- Category Service Functions ---

export async function createCategory(categoryData: NewCategory): Promise<any> { // Assuming Category type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('categories')
    .insert([categoryData])
    .select()
    .single();

  if (error) {
    throw new SupabaseServiceError('Failed to create category', error);
  }
  return data;
}

export async function updateCategory(id: string, updates: UpdatedCategory): Promise<any> { // Assuming Category type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new SupabaseServiceError('Failed to update category', error);
  }
  return data;
}

export async function deleteCategory(id: string): Promise<void> {
  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', id);

  if (error) {
    throw new SupabaseServiceError('Failed to delete category', error);
  }
}

export async function reorderCategories(items: SortOrderItem[]): Promise<void> {
  const { error } = await supabase.rpc('update_sort_order', {
    table_name: 'categories',
    items: JSON.stringify(items),
  });

  if (error) {
    throw new SupabaseServiceError('Failed to reorder categories', error);
  }
}

// --- Public Categories Service Functions ---

export async function fetchPublicCategories(branchId: string): Promise<any[]> { // Assuming Category type is not directly imported, using 'any' for now
  if (!branchId) {
    return [];
  }

  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('branch_id', branchId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    throw new SupabaseServiceError(`Error fetching public categories: ${error.message}`, error);
  }
  return data || [];
}

// --- Menu Item Service Functions ---

export async function createMenuItem(menuItemData: NewMenuItem): Promise<any> { // Assuming MenuItem type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('menu_items')
    .insert([menuItemData])
    .select()
    .single();

  if (error) {
    throw new SupabaseServiceError('Failed to create menu item', error);
  }
  return data;
}

export async function updateMenuItem(id: string, updates: UpdatedMenuItem): Promise<any> { // Assuming MenuItem type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('menu_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new SupabaseServiceError('Failed to update menu item', error);
  }
  return data;
}

export async function deleteMenuItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('menu_items')
    .delete()
    .eq('id', id);

  if (error) {
    throw new SupabaseServiceError('Failed to delete menu item', error);
  }
}

export async function reorderMenuItems(items: SortOrderItem[]): Promise<void> {
  const { error } = await supabase.rpc('update_sort_order', {
    table_name: 'menu_items',
    items: JSON.stringify(items),
  });

  if (error) {
    throw new SupabaseServiceError('Failed to reorder menu items', error);
  }
}

// --- Public Menu Item Service Functions ---

export async function fetchPublicMenuItems(params: { categoryId?: string; branchId?: string }): Promise<any[]> { // Assuming MenuItem type is not directly imported, using 'any' for now
  try {
    let query = supabase.from('menu_items').select('*').eq('is_available', true);

    if (params.categoryId) {
      query = query.eq('category_id', params.categoryId);
    } else if (params.branchId) {
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('id')
        .eq('branch_id', params.branchId);

      if (categoriesError) {
        throw new SupabaseServiceError(`Failed to fetch categories for public menu items: ${categoriesError.message}`, categoriesError);
      }

      const categoryIds = categories.map(c => c.id);

      if (categoryIds.length === 0) {
        return [];
      }

      query = query.in('category_id', categoryIds);
    } else {
      return []; // No categoryId or branchId provided
    }

    const { data, error } = await query.order('sort_order', { ascending: true });

    if (error) {
      throw new SupabaseServiceError(`Failed to fetch public menu items: ${error.message}`, error);
    }
    return data || [];
  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to fetch public menu items: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

export async function fetchBranchMenuItems(branchId: string): Promise<MenuItem[]> {
  try {
    const { data: categories, error: categoriesError } = await supabase
      .from('categories')
      .select('id')
      .eq('branch_id', branchId);

    if (categoriesError) {
      throw new SupabaseServiceError(`Failed to fetch categories: ${categoriesError.message}`, categoriesError);
    }

    const categoryIds = categories.map(c => c.id);

    if (categoryIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .in('category_id', categoryIds)
      .order('name', { ascending: true });

    if (error) {
      throw new SupabaseServiceError(`Failed to fetch menu items: ${error.message}`, error);
    }
    return (data as unknown as MenuItem[]) || [];
  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to fetch branch menu items: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

// --- Menu Item Ratings Service Functions ---

export async function fetchMenuItemRatings(menuItemId: string): Promise<any> { // Assuming MenuItemRating type is not directly imported, using 'any' for now
  if (!menuItemId) {
    return {
      averageRating: 0,
      totalRatings: 0,
      recentReviews: []
    };
  }

  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .eq('menu_item_id', menuItemId)
    .eq('feedback_type', 'rating')
    .not('rating', 'is', null)
    .order('created_at', { ascending: false });

  if (error) {
    throw new SupabaseServiceError(`Error fetching menu item ratings: ${error.message}`, error);
  }

  const feedbackData = data || [];
  const totalRatings = feedbackData.length;
  const averageRating = totalRatings > 0 
    ? feedbackData.reduce((sum, r) => sum + (r.rating || 0), 0) / totalRatings 
    : 0;

  const recentReviews: any[] = feedbackData.slice(0, 5).map(r => ({ // Assuming Review type is not directly imported, using 'any' for now
    id: r.id,
    rating: r.rating || 0,
    comment: r.comment,
    customer_name: r.customer_name,
    created_at: r.created_at || ''
  }));

  return {
    averageRating,
    totalRatings,
    recentReviews
  };
}

// --- Public Pop Offer Service Functions ---

export async function fetchPublicPopOffer(branchId: string): Promise<any | null> { // Assuming PopOffer type is not directly imported, using 'any' for now
  if (!branchId) {
    return null;
  }

  const { data, error } = await supabase
    .from('pop_offers')
    .select('*')
    .eq('branch_id', branchId)
    .lte('start_time', new Date().toISOString())
    .gte('end_time', new Date().toISOString())
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new SupabaseServiceError(`Error fetching public pop offer: ${error.message}`, error);
  }

  return data || null;
}

// --- Pop Offer Service Functions ---

export async function fetchPopOffer(branchId: string, userId: string): Promise<PopOfferRow | null> {
  const { data, error } = await supabase
    .from('pop_offers')
    .select('*')
    .eq('branch_id', branchId)
    .maybeSingle();

  if (error) {
    throw new SupabaseServiceError(`Error fetching pop offer: ${error.message}`, error);
  }
  return data;
}

export async function archivePopOffer(offerId: string): Promise<void> {
  const { error } = await supabase.rpc('archive_and_delete_expired_offer', {
    offer_id: offerId,
  });
  if (error) {
    throw new SupabaseServiceError(`Error archiving offer: ${error.message}`, error);
  }
}

export async function upsertPopOffer(offerData: Partial<any> & { branch_id: string; user_id: string }): Promise<PopOfferRow> { // Assuming PopOffer type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('pop_offers')
    .upsert(offerData, { onConflict: 'branch_id' })
    .select()
    .single();

  if (error) {
    throw new SupabaseServiceError(`Error upserting pop offer: ${error.message}`, error);
  }
  return data;
}

export async function deletePopOffer(offerId: string): Promise<void> {
  const { error } = await supabase
    .from('pop_offers')
    .delete()
    .eq('id', offerId);

  if (error) {
    throw new SupabaseServiceError(`Error deleting pop offer: ${error.message}`, error);
  }
}

export async function uploadPopOfferImage(file: File, branchId: string, userId: string): Promise<string> {
  const bucketName = 'menu-images';
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/${branchId}/pop-offer-${Date.now()}.${fileExt}`;
  
  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(fileName, file, { upsert: true });

  if (uploadError) {
    throw new SupabaseServiceError(`Error uploading image: ${uploadError.message}`, uploadError);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(bucketName)
    .getPublicUrl(fileName);
    
  return publicUrl;
}

// --- Archived Pop Offer Service Functions ---

export async function fetchArchivedPopOffers(branchId: string): Promise<any[]> { // Assuming ArchivedPopOffer type is not directly imported, using 'any' for now
  if (!branchId) {
    return [];
  }

  const { data, error } = await supabase
    .from('pop_offers_archive')
    .select('*')
    .eq('branch_id', branchId)
    .order('archived_at', { ascending: false });

  if (error) {
    throw new SupabaseServiceError(`Error fetching archived offers: ${error.message}`, error);
  }
  return data || [];
}

// --- Menu Uploads Service Functions ---

export async function fetchMenuUploads(branchId: string): Promise<any[]> { // Assuming MenuUpload type is not directly imported, using 'any' for now
  const { data, error } = await supabase
    .from('menu_uploads')
    .select('*')
    .eq('branch_id', branchId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new SupabaseServiceError(`Error fetching menu uploads: ${error.message}`, error);
  }

  return (data || []).map(item => ({
    ...item,
    status: item.status as 'processing' | 'completed' | 'failed',
    extracted_data: item.extracted_data as any | null, // Assuming ExtractedMenuData type is not directly imported, using 'any' for now
  }));
}

export async function uploadMenuFile(file: File, branchId: string): Promise<any> { // Assuming MenuUpload type is not directly imported, using 'any' for now
  try {
    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${branchId}/${fileName}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('menu-uploads')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw new SupabaseServiceError(`Error uploading file to storage: ${uploadError.message}`, uploadError);
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('menu-uploads')
      .getPublicUrl(filePath);

    // Create upload record
    const { data: uploadRecord, error: recordError } = await supabase
      .from('menu_uploads')
      .insert({
        branch_id: branchId,
        file_name: file.name,
        file_url: publicUrl,
        file_type: file.type,
        status: 'processing'
      })
      .select()
      .single();

    if (recordError) {
      throw new SupabaseServiceError(`Error creating menu upload record: ${recordError.message}`, recordError);
    }

    // Trigger extraction function
    const { error: functionError } = await supabase.functions.invoke('extract-menu', {
      body: {
        uploadId: uploadRecord.id,
        fileUrl: publicUrl
      }
    });

    if (functionError) {
      console.error('Function invocation error for extract-menu:', functionError);
      // Don't throw here, let the background process handle it
    }

    return uploadRecord as any; // Assuming MenuUpload type is not directly imported, using 'any' for now

  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to upload menu file: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

export async function deleteMenuUpload(uploadId: string): Promise<void> {
  try {
    // First get the upload record to get the file path
    const { data: upload, error: fetchError } = await supabase
      .from('menu_uploads')
      .select('*')
      .eq('id', uploadId)
      .single();

    if (fetchError) {
      throw new SupabaseServiceError(`Error fetching upload record for deletion: ${fetchError.message}`, fetchError);
    }

    // Extract the file path from the URL
    const fileUrl = new URL(upload.file_url);
    const filePath = fileUrl.pathname.split('/').slice(-2).join('/'); // Gets branchId/filename

    // Delete the file from storage
    const { error: storageError } = await supabase.storage
      .from('menu-uploads')
      .remove([filePath]);

    if (storageError) {
      throw new SupabaseServiceError(`Error deleting file from storage: ${storageError.message}`, storageError);
    }

    // Delete the database record
    const { error: deleteError } = await supabase
      .from('menu_uploads')
      .delete()
      .eq('id', uploadId);

    if (deleteError) {
      throw new SupabaseServiceError(`Error deleting menu upload record: ${deleteError.message}`, deleteError);
    }
  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to delete menu upload: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

// --- Order Service Functions ---

export async function fetchOrders(branchId?: string): Promise<Order[]> {
  let query = supabase
    .from('orders')
    .select(`
      id,
      branch_id,
      customer_name,
      customer_phone,
      customer_email,
      order_number,
      status,
      total_amount,
      notes,
      created_at,
      updated_at,
      customer_address,
      order_type,
      table_number,
      payment_method,
      delivery_fee,
      estimated_delivery_time,
      service_amount,
      tax_amount,
      subtotal_amount,
      order_items (
        id,
        order_id,
        menu_item_id,
        quantity,
        unit_price,
        total_price,
        notes,
        is_acknowledged,
        is_removed,
        menu_items (
          name,
          description
        )
      )
    `)
    .order('created_at', { ascending: false })
    .limit(100); // Added limit to prevent high egress

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  const { data, error } = await query;

  if (error) {
    throw new SupabaseServiceError(`Error fetching orders: ${error.message}`, error);
  }
  return data || [];
}

export async function updateOrderStatus(orderId: string, status: string): Promise<void> {
  const { error } = await supabase
    .from('orders')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', orderId);

  if (error) {
    throw new SupabaseServiceError(`Error updating order status: ${error.message}`, error);
  }
}

export async function cancelOrder(orderId: string, reason?: string): Promise<void> {
  console.log('[cancelOrder] Cancelling order:', orderId)
  const { error } = await supabase
    .from('orders')
    .update({ 
      status: 'cancelled', 
      updated_at: new Date().toISOString(),
      notes: reason ? `Cancelled: ${reason}` : 'Order cancelled'
    })
    .eq('id', orderId);

  if (error) {
    console.error('[cancelOrder] Error cancelling order:', error)
    throw new SupabaseServiceError(`Error cancelling order: ${error.message}`, error);
  }

  // Fetch the order to get table_session_id
  const { data: order, error: fetchError } = await supabase
    .from('orders')
    .select('table_session_id')
    .eq('id', orderId)
    .single();
  if (fetchError) {
    console.error('[cancelOrder] Error fetching order after cancel:', fetchError)
    return;
  }
  console.log('[cancelOrder] Fetched order for session:', order)
  if (order?.table_session_id) {
    console.log('[cancelOrder] Completing table session:', order.table_session_id)
    await completeTableSession(order.table_session_id, 'cancelled');
  } else {
    console.log('[cancelOrder] No table_session_id found for order:', orderId)
  }
}

// --- Logo Upload Service Functions ---

export async function uploadLogo(file: File): Promise<string> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `restaurant-logos/${fileName}`;

    const { data, error: uploadError } = await supabase.storage
      .from('restaurant-assets')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw new SupabaseServiceError(`Error uploading logo: ${uploadError.message}`, uploadError);
    }

    const { data: urlData } = supabase.storage
      .from('restaurant-assets')
      .getPublicUrl(filePath);

    if (!urlData.publicUrl) {
      throw new SupabaseServiceError('Failed to get public URL for uploaded logo.');
    }

    return urlData.publicUrl;
  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to upload logo: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

// --- Menu Item Image Upload Service Functions ---

export async function uploadMenuItemImage(file: File): Promise<string> {
  try {
    // Check if bucket exists
    const { data: buckets, error: listError } = await supabase
      .storage
      .listBuckets();

    if (listError) {
      throw new SupabaseServiceError(`Error listing storage buckets: ${listError.message}`, listError);
    }

    const menuItemImagesBucket = buckets.find(b => b.name === 'menu-item-images');

    if (!menuItemImagesBucket) {
      // Create bucket if it doesn't exist
      const { error: createError } = await supabase
        .storage
        .createBucket('menu-item-images', {
          public: true,
          fileSizeLimit: 5242880, // 5MB
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp']
        });

      if (createError) {
        throw new SupabaseServiceError(`Error creating menu-item-images bucket: ${createError.message}`, createError);
      }
    }
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `items/${fileName}`;
    
    const { data, error: uploadError } = await supabase.storage
      .from('menu-item-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw new SupabaseServiceError(`Error uploading menu item image: ${uploadError.message}`, uploadError);
    }

    const { data: urlData } = supabase.storage
      .from('menu-item-images')
      .getPublicUrl(filePath);

    if (!urlData.publicUrl) {
      throw new SupabaseServiceError('Failed to get public URL for uploaded menu item image.');
    }

    return urlData.publicUrl;
  } catch (error: unknown) {
    throw new SupabaseServiceError(`Failed to upload menu item image: ${error instanceof Error ? error.message : 'An unknown error occurred'}`, error);
  }
}

// --- Menu Item Variant Service Functions ---

import type { MenuItemVariant } from '@/types'

export async function fetchVariantsByMenuItem(menuItemId: string): Promise<MenuItemVariant[]> {
  if (!menuItemId) return []
  const { data, error } = await supabase
    .from('menu_item_variants')
    .select('*')
    .eq('menu_item_id', menuItemId)
    .order('price', { ascending: true })
  if (error) throw new SupabaseServiceError('Failed to fetch menu item variants', error)
  return data || []
}

export async function createMenuItemVariant(variant: NewMenuItemVariant): Promise<MenuItemVariant> {
  const { data, error } = await supabase
    .from('menu_item_variants')
    .insert([{
      menu_item_id: variant.menu_item_id,
      size_name: variant.size_name,
      price: Number(variant.price),
      description: variant.description || null
    }])
    .select()
    .single()
  if (error) throw new SupabaseServiceError('Failed to create menu item variant', error)
  return data
}

export async function updateMenuItemVariant(variantId: string, updates: Partial<Pick<MenuItemVariant, 'size_name' | 'price' | 'description'>>): Promise<MenuItemVariant> {
  const { data, error } = await supabase
    .from('menu_item_variants')
    .update(updates)
    .eq('id', variantId)
    .select()
    .single()
  if (error) throw new SupabaseServiceError('Failed to update menu item variant', error)
  return data
}

export async function deleteMenuItemVariant(variantId: string): Promise<void> {
  const { error } = await supabase
    .from('menu_item_variants')
    .delete()
    .eq('id', variantId)
  if (error) throw new SupabaseServiceError('Failed to delete menu item variant', error)
}

// Optimized order creation function for better performance with table session support
export async function createOrderWithCustomerIdOptimized(orderData: {
  branchId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerAddress?: string;
  notes?: string;
  orderType?: 'pickup' | 'delivery' | 'dine_in';
  tableNumber?: string;
  deliveryZoneId?: string;
  deliveryFee?: number;
  estimatedDeliveryTime?: string;
  items: Array<{
    menuItemId: string;
    variantId?: string;
    quantity: number;
    unitPrice: number;
    notes?: string;
  }>;
  customerId: string;
}) {
  console.log('🆕 [Order Creation] Starting order creation process:', {
    orderType: orderData.orderType,
    tableNumber: orderData.tableNumber,
    itemsCount: orderData.items.length
  });

  let tableSessionId: string | null = null;

  // Handle table session for dine-in orders FIRST
  if (orderData.orderType === 'dine_in' && orderData.tableNumber) {
    try {
      console.log(`🔍 [Table Session] Looking for existing session for table ${orderData.tableNumber}`);
      
      // Get or create table session using the database function
      const { data: sessionId, error: sessionError } = await supabase.rpc(
        'get_or_create_table_session',
        {
          p_branch_id: orderData.branchId,
          p_table_number: orderData.tableNumber
        }
      );

      if (sessionError) {
        console.error('❌ [Table Session] Failed to get/create table session:', sessionError);
        // Continue without table session if there's an error
      } else {
        tableSessionId = sessionId;
        console.log(`✅ [Table Session] Session ${tableSessionId} created/retrieved for table ${orderData.tableNumber}`);
      }
    } catch (error) {
      console.error('❌ [Table Session] Error handling table session:', error);
      // Continue without table session if there's an error
    }
  }

  // If we have a table session, check for existing active orders
  if (tableSessionId) {
    console.log(`🔍 [Table Session] Checking for existing active orders in session ${tableSessionId}`);
    
    // Try to find existing active orders
    const { data: existingOrders, error: existingOrdersError } = await supabase
      .from('orders')
      .select('id, order_number, total_amount, status')
      .eq('table_session_id', tableSessionId)
      .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
      .order('created_at', { ascending: false })
      .limit(1);

    if (existingOrdersError) {
      console.error('❌ [Table Session] Error checking for existing orders:', existingOrdersError);
    } else if (existingOrders && existingOrders.length > 0) {
      // Add items to existing order
      const existingOrder = existingOrders[0];
      console.log(`✅ [Table Session] Found existing order ${existingOrder.order_number} (${existingOrder.status}) - adding items to it`);

      // Calculate new total amount
      const newItemsTotal = orderData.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
      const newTotalAmount = existingOrder.total_amount + newItemsTotal + (orderData.deliveryFee || 0);

      console.log(`💰 [Table Session] Updating total: ${existingOrder.total_amount} + ${newItemsTotal} = ${newTotalAmount}`);

      // Update the existing order's total amount
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          total_amount: newTotalAmount,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingOrder.id);

      if (updateError) {
        throw new SupabaseServiceError('Failed to update existing order', updateError);
      }

      // Insert new order items
      const orderItems = orderData.items.map(item => ({
        order_id: existingOrder.id,
        branch_id: orderData.branchId, // Include branch_id for proper filtering
        menu_item_id: item.menuItemId,
        menu_item_variant_id: item.variantId || null,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.unitPrice * item.quantity,
        notes: item.notes || null,
        added_at: new Date().toISOString(),
        is_acknowledged: false, // This ensures the new item indicator shows up
      }));
      
      const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
      if (itemsError) {
        throw new SupabaseServiceError('Failed to add items to existing order', itemsError);
      }

      console.log(`✅ [Table Session] Successfully added ${orderData.items.length} items to existing order ${existingOrder.order_number}`);
      
      // Return minimal order data for immediate UI feedback - avoid heavy queries
      return {
        id: existingOrder.id,
        order_number: existingOrder.order_number,
        total_amount: newTotalAmount,
        status: existingOrder.status
      } as unknown as Order;
    }
  }

  // No existing order found, create a new one using atomic RPC
  console.time('🆕 RPC Order Creation');
  console.log('🆕 [Order Creation] Creating new order (atomic)');
  const payload = {
    p_branch_id: orderData.branchId,
    p_customer_name: orderData.customerName,
    p_customer_phone: orderData.customerPhone,
    p_customer_email: orderData.customerEmail ?? null,
    p_customer_address: orderData.customerAddress ?? null,
    p_customer_id: orderData.customerId,
    p_notes: orderData.notes ?? null,
    p_order_type: orderData.orderType ?? 'pickup',
    p_table_number: orderData.tableNumber ? parseInt(orderData.tableNumber) : null,
    p_delivery_zone_id: orderData.deliveryZoneId ?? null,
    p_delivery_fee: orderData.deliveryFee ?? 0,
    p_estimated_delivery_time: orderData.estimatedDeliveryTime ?? null,
    p_items: orderData.items.map(i => ({
      menuItemId: i.menuItemId,
      variantId: i.variantId || null,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      notes: i.notes || null
    }))
  } as const;

  const { data: newOrderId, error: rpcError } = await supabase.rpc('create_order_with_items_atomic', payload);
  console.timeEnd('🆕 RPC Order Creation');
  
  if (rpcError || !newOrderId) {
    throw new SupabaseServiceError('Failed to create order atomically', rpcError);
  }

  // Return minimal order data for immediate UI feedback - avoid heavy queries
  return {
    id: newOrderId,
    order_number: `ORD-${Date.now()}`, // Temporary order number for UI
    total_amount: orderData.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0) + (orderData.deliveryFee || 0),
    status: 'pending'
  } as unknown as Order;
}

// Keep original function name for backward compatibility
export const createOrderWithCustomerId = createOrderWithCustomerIdOptimized;

export async function fetchOrdersByCustomerId(customerId: string, branchId?: string) {
  let query = supabase
    .from('orders')
    .select(`
      id,
      branch_id,
      customer_name,
      customer_phone,
      customer_email,
      customer_address,
      customer_id,
      order_number,
      status,
      total_amount,
      notes,
      created_at,
      updated_at,
      order_items (
        id,
        order_id,
        menu_item_id,
        quantity,
        unit_price,
        total_price,
        notes,
        created_at,
        menu_items (
          name,
          description,
          image_url
        )
      ),
      delivery_assignments!delivery_assignments_order_id_fkey (
        id,
        assignment_status,
        driver_id,
        assigned_at,
        accepted_at,
        picked_up_at,
        in_transit_at,
        delivered_at,
        driver:delivery_drivers (
          id,
          name,
          phone,
          current_status
        )
      )
    `)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (branchId) {
    query = query.eq('branch_id', branchId);
  }

  const { data, error } = await query;
  if (error) throw new SupabaseServiceError('Failed to fetch customer orders', error);
  return data;
} 

// Add a function to complete a session and mark the table_sessions row as completed
export async function completeTableSession(tableSessionId: string, finalStatus: 'completed' | 'cancelled' = 'completed') {
  console.log(`[completeTableSession] ${finalStatus === 'cancelled' ? 'Cancelling' : 'Completing'} session:`, tableSessionId)
  
  try {
    // Call the database function to complete the session
    const { error } = await supabase.rpc('complete_table_session', {
      p_session_id: tableSessionId,
      p_final_status: finalStatus
    });

    if (error) {
      console.error('[completeTableSession] Error calling database function:', error)
      throw new SupabaseServiceError(`Failed to complete table session: ${error.message}`, error);
    }

    console.log(`[completeTableSession] Session marked as ${finalStatus}:`, tableSessionId)
  } catch (err) {
    console.error('[completeTableSession] Error completing session:', err)
    throw err;
  }
} 

// Function to add items to existing order or create new order for table sessions
export async function addItemsToTableSessionOrCreateOrder(orderData: {
  branchId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  customerAddress?: string;
  notes?: string;
  orderType?: 'pickup' | 'delivery' | 'dine_in';
  tableNumber?: string;
  deliveryZoneId?: string;
  deliveryFee?: number;
  estimatedDeliveryTime?: string;
  items: Array<{
    menuItemId: string;
    variantId?: string;
    quantity: number;
    unitPrice: number;
    notes?: string;
  }>;
  customerId: string;
}) {
  console.log('🔄 [Table Session] Starting order creation process:', {
    orderType: orderData.orderType,
    tableNumber: orderData.tableNumber,
    itemsCount: orderData.items.length
  });

  // The database function create_order_with_items_atomic now handles:
  // 1. Getting/creating table sessions
  // 2. Checking for existing active orders (with row locks to prevent race conditions)
  // 3. Adding items to existing orders OR creating new orders
  // This eliminates client-side race conditions
  
  const payload = {
    p_branch_id: orderData.branchId,
    p_customer_name: orderData.customerName,
    p_customer_phone: orderData.customerPhone,
    p_customer_email: orderData.customerEmail ?? null,
    p_customer_address: orderData.customerAddress ?? null,
    p_customer_id: orderData.customerId,
    p_notes: orderData.notes ?? null,
    p_order_type: orderData.orderType ?? 'pickup',
    p_table_number: orderData.tableNumber ?? null,
    p_delivery_zone_id: orderData.deliveryZoneId ?? null,
    p_delivery_fee: orderData.deliveryFee ?? 0,
    p_estimated_delivery_time: orderData.estimatedDeliveryTime ?? null,
    p_items: orderData.items.map(i => ({
      menuItemId: i.menuItemId,
      variantId: i.variantId || null,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      notes: i.notes || null
    }))
  } as const;

  const { data: newOrderId, error: rpcError } = await supabase.rpc('create_order_with_items_atomic', payload);
  if (rpcError || !newOrderId) {
    throw new SupabaseServiceError('Failed to create order atomically', rpcError);
  }

  // Return minimal order data for immediate UI feedback - reduce database load
  return { 
    id: newOrderId, 
    order_number: `ORD-${Date.now()}`, // Temporary order number for UI
    total_amount: orderData.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0) + (orderData.deliveryFee || 0)
  } as unknown as Order;
} 